import { Box, List, ListItemButton, ListItemIcon, ListItemText, styled, useTheme } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import Stack from '@mui/material/Stack';
import { VyIcon } from '@weft/icons';
import { useActivate } from 'apps/app/src/hooks/use-activate';
import cn from 'classnames';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isSandboxEnvironment } from '../../../utils/env-indicator';
import { getLocalStorageItem, setLocalStorageItem } from '../../../utils/local-storage';
import { HelpIcon } from '../../icons/HelpIcon';
import { IntegrationsIcon } from '../../icons/IntegrationsIcon';
import { SettingsIcon } from '../../icons/SettingsIcon';
import { ListItemButtonLink } from './navbar-components/ListItemButtonLink';
import { NavbarDrawer } from './navbar-components/NavbarDrawer';
import { NavbarList } from './navbar-components/NavbarList/NavbarList';
import { NavbarLogo } from './navbar-components/NavbarLogo';
import { NAVIGATION_LABELS } from './navbar-types/navigation-label';
import { HELP_URL } from './side-navbar-consts';

const StyledListItemButton = styled(ListItemButton)({
  color: blueGrey['300'],
  '&:hover': {
    backgroundColor: blueGrey['900'],
  },
});

export const SideNavbar: React.FC = () => {
  const globalTheme = useTheme();

  const location = useLocation();

  const localStorageCollapsed = getLocalStorageItem<{ collapsed: boolean }>('side-navbar-collapsed');
  const [collapsed, setCollapsed] = useState(localStorageCollapsed?.collapsed ?? false);
  const [isPreview, openPreview, closePreview] = useActivate(false);

  const showFullNav = !collapsed || (collapsed && isPreview);

  const getNavbarItemColor = (href: string, atStart = false) => {
    if (atStart) {
      return location.pathname.startsWith(href) ? globalTheme.palette.tertiary.main : blueGrey['300'];
    }

    return location.pathname.includes(href) ? globalTheme.palette.tertiary.main : blueGrey['300'];
  };

  return (
    <div>
      {collapsed && <div className="h-full w-[72px]"/>}

      <NavbarDrawer
        variant="permanent"
        onMouseEnter={() => collapsed && openPreview()}
        onMouseLeave={() => collapsed && closePreview()}
        className={cn({ 'fixed z-[9999]': collapsed })}
        open={showFullNav}
      >
        <Stack sx={theme => ({
          height: '100vh',
          backgroundColor: theme.palette.common.black,
          overflow: 'hidden',
          width: '248px',
          position: 'relative',
          paddingTop: isSandboxEnvironment() ? '52px' : '0',
        })}
        >
          <NavbarLogo showFullNav={showFullNav}/>

          <NavbarList showFullNav={showFullNav}/>

          <Stack sx={{
            flex: 1,
            justifyContent: 'end',
          }}
          >
            <List
              component="nav"
              sx={{
                paddingY: '16px',
                marginTop: 'auto',
                borderTop: '1px solid #FFFFFF29',
                borderBottom: '1px solid #FFFFFF29',
              }}
            >
              <ListItemButtonLink to="/integrations">
                <ListItemIcon>
                  <IntegrationsIcon color={getNavbarItemColor('/integrations')}/>
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ variant: 'body1', color: getNavbarItemColor('/integrations') }}>
                  {NAVIGATION_LABELS.INTEGRATIONS}
                </ListItemText>
              </ListItemButtonLink>

              <ListItemButtonLink
                to={HELP_URL}
                target="_blank"
              >
                <ListItemIcon>
                  <HelpIcon color={blueGrey['300']}/>
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ variant: 'body1' }}>
                  {NAVIGATION_LABELS.HELP}
                </ListItemText>
              </ListItemButtonLink>

              <ListItemButtonLink to="/settings">
                <ListItemIcon>
                  <SettingsIcon color={getNavbarItemColor('/settings')}/>
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ variant: 'body1', color: getNavbarItemColor('/settings') }}>
                  {NAVIGATION_LABELS.SETTINGS}
                </ListItemText>
              </ListItemButtonLink>
            </List>

            <Box sx={{ paddingY: '16px' }}>
              <StyledListItemButton onClick={() => {
                setCollapsed(value => {
                  if (!value) {
                    closePreview();
                  }

                  setLocalStorageItem('side-navbar-collapsed', { collapsed: !value });

                  return !value;
                });
              }}
              >
                <ListItemIcon>
                  <VyIcon
                    icon="ArrowLineLeft"
                    color={blueGrey[300]}
                    style={{
                      transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.1s',
                    }}
                  />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ variant: 'body1' }}>
                  Collapse
                </ListItemText>
              </StyledListItemButton>
            </Box>
          </Stack>
        </Stack>
      </NavbarDrawer>
    </div>
  );
};